/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import {
    // getEstimatedAvailabilityAsync,
    searchByCriteriaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getInventoryLevelRangesAsync, getProductEligibilityAsync } from './DataActionExtension.g';
import { IItemInventoryLevelProfileEntity } from './DataServiceEntities.g';
import { EligibleItems } from '../modules/eve-quick-order/eve-quick-order';
import getExtensionPropertyValueByKey from '../utils/getExtensionPropertyValueByKey';
// TODO: move this to utils
import { CommercePropertyValueEnum } from '../modules/allowable-freight/utils/CommercePropertyValueEnum';

/**
 * GetPartState Input Action
 */
export class GetPartStateInput implements Msdyn365.IActionInput {
    public itemIds: string[];
    public addressId: number;
    public cartId: string;
    public channelId: number;
    constructor(itemIds: string[], addressId: number, cartId: string, channelId: number) {
        this.itemIds = itemIds;
        this.addressId = addressId;
        this.cartId = cartId;
        this.channelId = channelId;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `ITEMIDS:${this.itemIds.join(';')},${this.channelId},${this.cartId}`;
    public getCacheObjectType = () => `PART-STATE`;
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IGetPartStateData {
    productResults: IProductSearchResultWithState[];
}

export interface IProductSearchResultWithState extends ProductSearchResult {
    // state?: IProductState;
    oosUpperBoundLimit?: number;
    isEligible?: boolean | undefined;
}

// export enum IProductState {
//     NOTFOUND = 0,
//     OOS = 1,
//     AVAIL = 2
// }

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const itemIds = args.requestContext.query?.itemIds;
    const addressId = args.requestContext.query?.addressId;
    const cartId = args.requestContext.query?.cartId;
    if (!itemIds || !addressId || !cartId) {
        throw Error('ItemIds, addressId or cartId is undefined');
    }
    const itemIdsArr = itemIds.split(',');
    const channelId = args.requestContext.channel!.RecordId;

    return new GetPartStateInput(itemIdsArr, parseInt(addressId, 10), cartId, channelId);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: GetPartStateInput, ctx: Msdyn365.IActionContext): Promise<IGetPartStateData> {
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    // TODO: Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<IGetPartStateData[]>('/get/example/id/1', 'get');

    const productSearchCriteria: ProductSearchCriteria = {
        ItemIds: input.itemIds.map(pid => {
            return { ItemId: pid };
        }),
        Context: {
            CatalogId: 0,
            ChannelId: input.channelId
        }
    };
    const criteriaResponse = await searchByCriteriaAsync({ callerContext: ctx }, productSearchCriteria);

    if (!criteriaResponse) {
        throw Error(' No items ');
    }
    const criteriaItems = criteriaResponse
        ?.filter(c => c.ItemId)
        .map(c => {
            return c;
        }); // itemId is here because we filtered out undefined.

    // console.log(criteriaItems);

    // const supersessionData = await getSupersessionAsync(
    //     { callerContext: ctx },
    //     criteriaResponse.map(i => i.ItemId!),
    //     []
    // );

    const inventoryLevelsResponse = getInventoryLevelRangesAsync(
        { callerContext: ctx },
        criteriaItems.map(pr => pr.ItemId!)
    );
    const list: EligibleItems = [];
    // const addressId: string = ctx.requestContext.cookies.getCheckoutCartCookie();
    const eligibility = getProductEligibilityAsync(
        { callerContext: ctx },
        criteriaItems.map(p => p.ItemId!),
        input.addressId,
        input.cartId
    ).then((result: any) => {
        result.forEach((i: any) => {
            // TODO: Don't use hardcoded key
            const id = getExtensionPropertyValueByKey(i.ExtensionProperties, 'ItemId', CommercePropertyValueEnum.string);
            // console.log(`Item ID: ${id} is ${i.IsEligible ? 'eligible' : 'NOT eligible'} - IsEligible = ${i.IsEligible}`);
            list.push({
                ItemId: String(id),
                IsEligible: i.IsEligible
            });
        });
    });
    // await Promise.all([inventory, eligibility]);
    // await Promise.all([inventoryLevelsResponse, eligibility]);
    await Promise.all([eligibility, inventoryLevelsResponse]);

    //// Can't throw error for inventory level response as it's possible for some valid models to have no inventory in our system
    // if (!inventoryLevelsResponse || inventoryLevelsResponse.length === 0) {
    //     throw Error('No Inventory Level Data');
    // }

    // let profileData: IItemInventoryLevelProfileEntity | undefined;
    // let ProductResultsWithState: IProductSearchResultWithState[];

    // let errstring = JSON.stringify(inventoryLevelsResponse[0].Profile);
    // throw Error (errstring);
    const ProductResultsWithState: IProductSearchResultWithState[] = criteriaItems.map(cr => {
        // console.log(cr);
        let invProfOos: IItemInventoryLevelProfileEntity | undefined;
        inventoryLevelsResponse.result?.map(ir => {
            const tempInvRange = ir.Profile?.find(p => p.ItemId === cr.ItemId && p.Code === 'OOS');
            if (tempInvRange) {
                invProfOos = tempInvRange;
            }
        });
        const oosUpperBound =
            invProfOos?.hasOwnProperty('UpperBoundInclusive') && invProfOos?.UpperBoundInclusive !== undefined
                ? invProfOos.UpperBoundInclusive
                : 0;
        const productEligibility = list.find(i => i.ItemId === cr.ItemId);
        const isEligible = productEligibility && productEligibility.IsEligible;
        const newCr: IProductSearchResultWithState = { ...cr, oosUpperBoundLimit: oosUpperBound, isEligible: isEligible };
        return newCr;
    });

    return { productResults: ProductResultsWithState };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetPartStateData>>action,
    // TODO: Give your data action a unique id
    id: 'GetPartState',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
